import { Text } from "@solomon-labs/design/components/ui";
import React from "react";

import usdvLogoUrl from "../../../assets/logos/usdv-logomark.png";
import { NumberField } from "../../../components/NumberField";
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue
} from "../../../components/ui/select";

import { BalanceAmountTranche } from "./BalanceAmountTranche";
import { parseNewUserConfig, serializeNewUserConfig } from "./policyFixtures";
import { TimeHeldTranche } from "./TimeHeldTranche";
import type { ConfigurablePolicy } from "./types";
import { WalletListInput } from "./WalletListInput";

/** Forced-interaction phase the cursor is painting on a config control. */
export type PolicyDemoPhase = "hover" | "press";

/**
 * Sim-only demo seam for the cursor-driven Policies sim (see
 * {@link ProgramPoliciesSim}). When set, the config's primary VALUE control
 * paints its forced hover/focus visual (CSS pseudo-states can't be JS-driven)
 * so a scripted cursor hovering/clicking it reads as believable. Production
 * callers omit it → the config renders exactly as before.
 */
export interface PolicyConfigDemo {
    /** Force the primary value control's hover/focus visual (scalar / select /
     *  New User bonus / address input). */
    valuePhase?: PolicyDemoPhase | null;
    /** New User policies only — force the "Joined within" field's hover/focus
     *  visual (its own control, so the cursor can visibly type it too). */
    joinedWithinPhase?: PolicyDemoPhase | null;
    /** Tranche policies only — the row whose AMOUNT field the cursor is on. */
    trancheAmountRow?: number | null;
    /** Tranche policies only — the forced phase on the active amount field. */
    trancheAmountPhase?: PolicyDemoPhase | null;
    /** Tranche policies only — the row whose MULTIPLIER field the cursor is on. */
    trancheMultiplierRow?: number | null;
    /** Tranche policies only — the forced phase on the active multiplier field. */
    trancheMultiplierPhase?: PolicyDemoPhase | null;
    /** Force the tranche list's "+Add Tranche" button hover/press visual. */
    trancheAddPhase?: PolicyDemoPhase | null;
    /** Wallet policies only — force the mode ToggleMenu (Whitelist / Blacklist /
     *  KYC Only) hover/press visual on the `modeTarget` item. */
    modePhase?: PolicyDemoPhase | null;
    /** Wallet policies only — the mode value the cursor is on (the item that
     *  paints `modePhase`). */
    modeTarget?: string | null;
}

export interface PolicyConfigurationProps {
    policy: ConfigurablePolicy;
    value: number | string;
    onChange: (next: number | string) => void;
    /** Sim-only — drives forced hover/focus on the primary value control.
     *  Omit in production. */
    demo?: PolicyConfigDemo;
    /** Sim-only cursor key placed on the primary value control (the
     *  NumberField / Select trigger / New User bonus field / address
     *  input) so `useSimCursor().moveTo(key)` can travel to it. Inert in
     *  production. */
    valueCursorKey?: string;
    /** Sim-only cursor key placed on the New User "Joined within" field so the
     *  cursor can travel to + focus it (it types its own value). */
    joinedWithinCursorKey?: string;
    /** Sim-only cursor-key prefix for tranche rows — each row's amount field
     *  gets `${prefix}-amount-${i}` and its multiplier `${prefix}-mult-${i}`,
     *  so the cursor can travel to a specific row's amount or multiplier. */
    trancheRowCursorKeyPrefix?: string;
    /** Sim-only cursor key placed on the tranche "+Add Tranche" button. */
    trancheAddCursorKey?: string;
    /** Sim-only cursor-key prefix placed on each wallet-mode ToggleMenuItem
     *  (`<prefix>-<mode>`) so the cursor can travel to a specific mode. */
    modeCursorKeyPrefix?: string;
}

/**
 * PolicyConfiguration — type-driven value control for a configurable policy:
 * the slider / select / tranche / wallet input. A Domain Component shared by
 * the create-policy editor (`PolicyCreator`) and the per-policy inline edit on
 * the program detail (`PolicyRow`).
 */
export function PolicyConfiguration({
    policy,
    value,
    onChange,
    demo,
    valueCursorKey,
    joinedWithinCursorKey,
    trancheRowCursorKeyPrefix,
    trancheAddCursorKey,
    modeCursorKeyPrefix
}: PolicyConfigurationProps): React.ReactElement {
    const valueHover = demo?.valuePhase === "hover";
    const valuePress = demo?.valuePhase === "press";
    if (policy.kind === "slider") {
        // Empty ("" — the freshly-chosen state) renders the field BLANK; a
        // typed value renders as-is. The user types the threshold in.
        const isEmpty = value === "" || value === null || value === undefined;
        // Clamp to the policy's [min, max] on blur — NumberField owns only
        // digit filtering, so the domain clamp stays here in the caller. Skip
        // the clamp while empty so blurring an untouched field doesn't seed a
        // value.
        return (
            <div
                className="flex items-center gap-3"
                onBlur={() => {
                    if (isEmpty) return;
                    onChange(
                        Math.min(
                            policy.max,
                            Math.max(policy.min, Number(value))
                        )
                    );
                }}
            >
                <NumberField
                    data-sim-cursor-key={valueCursorKey}
                    forceHover={valueHover}
                    forceFocus={valuePress}
                    value={isEmpty ? null : Number(value)}
                    onChange={(n) => onChange(n === null ? "" : n)}
                    ariaLabel={policy.title}
                    placeholder="0"
                    className={policy.unit === "usdpt" ? "w-48" : "w-40"}
                    adornment={
                        policy.unit === "usdpt" ? (
                            <span className="flex items-center gap-1.5 shrink-0">
                                <img
                                    src={usdvLogoUrl}
                                    alt=""
                                    className="size-4"
                                />
                                <Text
                                    variant="small"
                                    render={<span />}
                                    className="text-foreground-neutral"
                                >
                                    USDv
                                </Text>
                            </span>
                        ) : undefined
                    }
                />
                {policy.unit !== "usdpt" && (
                    <Text
                        variant="fine-print"
                        render={<span />}
                        className="text-foreground-neutral shrink-0"
                    >
                        {policy.min.toLocaleString("en-US")}–
                        {policy.max.toLocaleString("en-US")}
                    </Text>
                )}
            </div>
        );
    }
    if (policy.kind === "select") {
        return (
            <Select
                value={String(value)}
                onValueChange={(v) => onChange(v ?? String(value))}
            >
                <SelectTrigger
                    className="w-56"
                    data-sim-cursor-key={valueCursorKey}
                    forceHover={valueHover}
                    forceFocus={valuePress}
                >
                    <SelectValue>
                        {() =>
                            policy.options.find(
                                (o) => o.value === String(value)
                            )?.label ?? String(value)
                        }
                    </SelectValue>
                </SelectTrigger>
                <SelectContent align="start" alignItemWithTrigger={false}>
                    {policy.options.map((o) => (
                        <SelectItem key={o.value} value={o.value}>
                            {o.label}
                        </SelectItem>
                    ))}
                </SelectContent>
            </Select>
        );
    }
    if (policy.kind === "new-user") {
        // Empty-preserving: null fields render BLANK so the user types the
        // bonus + recency window in (no seeded defaults).
        const config = parseNewUserConfig(value);
        return (
            <div className="flex flex-col gap-4">
                {/* Bonus amount — the one-time USDv payout per user. The
                    primary value control (carries the sim cursor key + forced
                    hover/focus), mirroring Minimum Threshold. */}
                <div className="flex flex-col gap-1.5">
                    <Text
                        variant="small"
                        render={<span />}
                        className="text-foreground-muted"
                    >
                        Bonus amount
                    </Text>
                    <NumberField
                        data-sim-cursor-key={valueCursorKey}
                        forceHover={valueHover}
                        forceFocus={valuePress}
                        value={config.bonusAmount}
                        onChange={(n) =>
                            onChange(
                                serializeNewUserConfig({
                                    ...config,
                                    bonusAmount: n
                                })
                            )
                        }
                        ariaLabel="Bonus amount"
                        placeholder="0"
                        className="w-48"
                        adornment={
                            <span className="flex items-center gap-1.5 shrink-0">
                                <img
                                    src={usdvLogoUrl}
                                    alt=""
                                    className="size-4"
                                />
                                <Text
                                    variant="small"
                                    render={<span />}
                                    className="text-foreground-neutral"
                                >
                                    USDv
                                </Text>
                            </span>
                        }
                    />
                </div>
                {/* Eligibility window — "joined within N days". */}
                <div className="flex flex-col gap-1.5">
                    <Text
                        variant="small"
                        render={<span />}
                        className="text-foreground-muted"
                    >
                        Joined within
                    </Text>
                    <NumberField
                        data-sim-cursor-key={joinedWithinCursorKey}
                        forceHover={demo?.joinedWithinPhase === "hover"}
                        forceFocus={demo?.joinedWithinPhase === "press"}
                        value={config.joinedWithinDays}
                        onChange={(n) =>
                            onChange(
                                serializeNewUserConfig({
                                    ...config,
                                    joinedWithinDays: n
                                })
                            )
                        }
                        ariaLabel="Joined within (days)"
                        placeholder="0"
                        className="w-48"
                        adornment={
                            <Text
                                variant="small"
                                render={<span />}
                                className="text-foreground-neutral shrink-0"
                            >
                                days
                            </Text>
                        }
                    />
                </div>
            </div>
        );
    }
    if (policy.kind === "tranche") {
        const trancheSeams = {
            rowCursorKeyPrefix: trancheRowCursorKeyPrefix,
            activeAmountRow: demo?.trancheAmountRow ?? null,
            amountRowPhase: demo?.trancheAmountPhase ?? null,
            activeMultiplierRow: demo?.trancheMultiplierRow ?? null,
            multiplierRowPhase: demo?.trancheMultiplierPhase ?? null,
            addCursorKey: trancheAddCursorKey,
            addForceHover: demo?.trancheAddPhase === "hover",
            addForcePressed: demo?.trancheAddPhase === "press"
        };
        return policy.amountUnit === "usdpt" ? (
            <BalanceAmountTranche
                value={String(value)}
                onChange={(next) => onChange(next)}
                {...trancheSeams}
            />
        ) : (
            <TimeHeldTranche
                value={String(value)}
                onChange={(next) => onChange(next)}
                {...trancheSeams}
            />
        );
    }
    return (
        <WalletListInput
            placeholder={policy.placeholder}
            modes={policy.modes}
            value={String(value)}
            onChange={(next) => onChange(next)}
            addressCursorKey={valueCursorKey}
            addressForceHover={valueHover}
            addressForceFocus={valuePress}
            modeCursorKeyPrefix={modeCursorKeyPrefix}
            modeHoverValue={
                demo?.modePhase === "hover" ? (demo?.modeTarget ?? null) : null
            }
            modePressValue={
                demo?.modePhase === "press" ? (demo?.modeTarget ?? null) : null
            }
        />
    );
}
